/**
 * Browser pose engine: MediaPipe Pose Landmarker + torso densification.
 * Falls back gracefully if WASM model fails to load (demo/fixture mode).
 */

import type { CoachingResult, PoseLandmark, PoseMetrics, TorsoSegment } from "../biomechanics/types";
import {
  computePoseMetrics,
  torsoFromLandmarks,
} from "../biomechanics";
import { coachFromMetrics } from "../biomechanics/coaching";

export interface FrameAnalysis {
  landmarks: PoseLandmark[];
  torso: TorsoSegment;
  metrics: PoseMetrics;
  coaching: CoachingResult;
  timestampMs: number;
}

export interface PoseEngineOptions {
  /** CDN base for MediaPipe WASM assets */
  wasmRoot?: string;
  modelUrl?: string;
  intermediateCount?: number;
}

const DEFAULT_WASM =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm";
const DEFAULT_MODEL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";

export class PoseEngine {
  private landmarker: {
    detectForVideo: (
      video: HTMLVideoElement,
      timestamp: number,
    ) => { landmarks?: Array<Array<{ x: number; y: number; z?: number; visibility?: number }>> };
    detect: (
      image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
    ) => { landmarks?: Array<Array<{ x: number; y: number; z?: number; visibility?: number }>> };
    close: () => void;
  } | null = null;
  private ready = false;
  private loadError: string | null = null;
  private intermediateCount: number;

  constructor(private options: PoseEngineOptions = {}) {
    this.intermediateCount = options.intermediateCount ?? 5;
  }

  get isReady(): boolean {
    return this.ready;
  }

  get error(): string | null {
    return this.loadError;
  }

  async init(): Promise<void> {
    try {
      const vision = await import("@mediapipe/tasks-vision");
      const { PoseLandmarker, FilesetResolver } = vision;
      const wasmRoot = this.options.wasmRoot ?? DEFAULT_WASM;
      const fileset = await FilesetResolver.forVisionTasks(wasmRoot);
      this.landmarker = await PoseLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: this.options.modelUrl ?? DEFAULT_MODEL,
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.4,
        minPosePresenceConfidence: 0.4,
        minTrackingConfidence: 0.4,
      });
      this.ready = true;
      this.loadError = null;
    } catch (err) {
      this.ready = false;
      this.loadError = err instanceof Error ? err.message : String(err);
      console.warn("[PoseEngine] MediaPipe init failed; fixture mode only:", this.loadError);
    }
  }

  /**
   * Analyze raw landmarks (fixture / unit path) through the real pipeline.
   */
  analyzeLandmarks(landmarks: PoseLandmark[], timestampMs = 0): FrameAnalysis {
    const torso = torsoFromLandmarks(landmarks, {
      intermediateCount: this.intermediateCount,
    });
    const metrics = computePoseMetrics(landmarks, {
      torso,
      intermediateCount: this.intermediateCount,
    });
    const coaching = coachFromMetrics(metrics);
    return { landmarks, torso, metrics, coaching, timestampMs };
  }

  analyzeVideoFrame(video: HTMLVideoElement, timestampMs: number): FrameAnalysis | null {
    if (!this.landmarker) return null;
    const result = this.landmarker.detectForVideo(video, timestampMs);
    const lm = result.landmarks?.[0];
    if (!lm || lm.length < 25) return null;
    const landmarks: PoseLandmark[] = lm.map((p) => ({
      x: p.x,
      y: p.y,
      z: p.z,
      visibility: p.visibility,
    }));
    return this.analyzeLandmarks(landmarks, timestampMs);
  }

  close(): void {
    try {
      this.landmarker?.close();
    } catch {
      /* ignore */
    }
    this.landmarker = null;
    this.ready = false;
  }
}
